import { err, ok, type Result } from '../result.ts';
import { parseAuthScope, type AuthScope } from './scopes.ts';

/**
 * R9 — core models the *mode*, never the credential.
 *
 * A granted token is JSON with four fields: `session`, `scopes`, `expire`, `signature`. Two of
 * them (`session`, `signature`) **are** the secret. This type deliberately carries neither, so a
 * claims object can be put in a log, a state snapshot or an error UI without becoming a leak — the
 * raw token string stays in platform storage and is only ever read to build one header.
 */
export interface AuthTokenClaims {
  readonly scopes: readonly AuthScope[];
  /** Scope strings that failed to parse are kept verbatim: an unknown grant is still a grant. */
  readonly rawScopes: readonly string[];
  /** Absolute Unix epoch seconds. `null` when the instance minted the token without one. */
  readonly expireEpochSec: number | null;
}

export type AuthTokenProblem = 'not-an-object' | 'no-session' | 'no-signature';

/**
 * R4 — `JSON.parse` stays outside core; this reads the already-parsed value defensively.
 *
 * The presence of `session` and `signature` is checked but their values are dropped on the floor.
 * That is the whole point: this function is the boundary where a credential becomes a claim.
 */
export function parseAuthTokenClaims(raw: unknown): Result<AuthTokenClaims, AuthTokenProblem> {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return err('not-an-object');
  const record = raw as Record<string, unknown>;

  if (typeof record['session'] !== 'string' || record['session'] === '') return err('no-session');
  if (typeof record['signature'] !== 'string' || record['signature'] === '') return err('no-signature');

  const rawScopes: string[] = [];
  const scopes: AuthScope[] = [];
  const list = record['scopes'];
  if (Array.isArray(list)) {
    for (const entry of list) {
      if (typeof entry !== 'string') continue;
      rawScopes.push(entry);
      const scope = parseAuthScope(entry);
      if (scope !== null) scopes.push(scope);
    }
  }

  const expire = record['expire'];
  const expireEpochSec = typeof expire === 'number' && Number.isFinite(expire) ? expire : null;

  return ok({ scopes, rawScopes, expireEpochSec });
}

/**
 * Decidable client-side, and worth deciding: the instance's "expired" answer is a `403` whose body
 * is the only thing separating it from four unrelated failures. Checking the claim first turns a
 * guess into a fact, and lets the app re-authorize *before* a call fails.
 */
export function authTokenExpired(claims: AuthTokenClaims, nowEpochSec: number): boolean {
  return claims.expireEpochSec !== null && claims.expireEpochSec <= nowEpochSec;
}

/**
 * The under-decoded token from §8's double-encoding trap starts with a percent-encoded `{`.
 * A stored value that looks like this was decoded one time too few and will 403 on every call —
 * catching it at the storage boundary is cheaper than debugging it at the call site.
 */
export function looksPercentEncoded(token: string): boolean {
  return token.trimStart().startsWith('%7B') || token.trimStart().startsWith('%257B');
}
